import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Employee } from 'employee/Employee';
import { userEmailSelector } from 'auth/redux.selectors';
import { getEmployeeData } from 'profile/redux.actions';
import { usePermission } from 'permissions/hooks';

import { publishChangeRequest } from '../redux.actions';
import { employeeToFlat } from '../mappers';
import ChangeRequestComplete from './ChangeRequestComplete';

/**
 * Full-page "Request a Change" — reuses the ACTUAL employee-edit wizard
 * (General → Contact → Family → Identification → Academic → Others) instead of
 * a popup. It only differs from employee-edit in behaviour, injected as props:
 *   - prefilled from the signed-in employee's own record (employee/get/me)
 *   - "Next" just advances (no employee auto-save)
 *   - final submit creates a change request for admin approval
 *   - "Save And Exit" (like employee-edit) submits the request from any step,
 *     so a user changing only e.g. their address need not page to the end
 * The managerial "Manage" fields are not part of this wizard, so they are
 * naturally excluded.
 */
const ChangeRequestPage = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const ownEmail = useSelector(userEmailSelector);
    const email = searchParams.get('id') || ownEmail;
    const canDirect = usePermission('profile-change-request.update directly');
    // Remember the prefilled values so the submit can tell which list sections
    // actually changed (and skip the untouched ones).
    const initialFlatRef = useRef({});

    const publish = (props) =>
        dispatch(
            publishChangeRequest({ ...props, email, initialFlat: initialFlatRef.current })
        );
    const publishPartial = ({ handleComplete = () => {} } = {}) =>
        handleComplete({ error: false });
    const fetchInitial = (setValues) =>
        dispatch(
            getEmployeeData({
                payload: { email, isSelf: true },
                handleSubmit: (resp) => {
                    const flat = employeeToFlat(resp?.data || {});
                    initialFlatRef.current = flat;
                    setValues(flat);
                }
            })
        );

    return (
        <Employee
            mode='update'
            title={canDirect ? 'Edit Information' : 'Request a Change'}
            publish={publish}
            publishPartial={publishPartial}
            fetchInitial={fetchInitial}
            CompleteComponent={ChangeRequestComplete}
        />
    );
};

export default ChangeRequestPage;
